import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { fetchJson } from "../lib/api";
import { apiBase } from "../lib/apiBase";
import { resolveCanonicalSymbol, sortCanonicalSymbols } from "../utils/coinSymbols";

function formatUsd(value) {
    const numeric = Number(value);
    if (value == null || !Number.isFinite(numeric)) return "—";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(numeric);
}

function normalizedValuation(row) {
    const quantityRaw = row.position_quantity ?? row.base_asset_amount ?? row.units;
    const cashRaw = row.cash_usd ?? row.usd_cash ?? row.quote_amount;
    const priceRaw = row.current_price ?? row.price;
    const quantity = quantityRaw == null ? Number.NaN : Number(quantityRaw);
    const cash = cashRaw == null ? Number.NaN : Number(cashRaw);
    const price = priceRaw == null ? Number.NaN : Number(priceRaw);
    const safeQuantity = Number.isFinite(quantity) ? quantity : null;
    const safeCash = Number.isFinite(cash) ? cash : null;
    const safePrice = Number.isFinite(price) && price > 0 ? price : null;
    let positionValue = row.position_value_usd == null
        ? Number.NaN
        : Number(row.position_value_usd);
    if (!Number.isFinite(positionValue)) {
        positionValue = safeQuantity === 0
            ? 0
            : (safeQuantity != null && safePrice != null ? safeQuantity * safePrice : null);
    }
    let totalEquity = row.total_equity_usd == null
        ? Number.NaN
        : Number(row.total_equity_usd);
    if (!Number.isFinite(totalEquity)) {
        totalEquity = safeCash != null && positionValue != null ? safeCash + positionValue : null;
    }
    return {
        quantity: safeQuantity,
        cash: safeCash,
        price: safePrice,
        positionValue,
        totalEquity,
        priceUnavailable: Boolean(safeQuantity && safeQuantity > 0 && safePrice == null),
    };
}

export default function WalletTotalsPanel({ className = "" }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [mode, setMode] = useState("");
    const [note, setNote] = useState("");
    const [operatorNote, setOperatorNote] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await fetchJson(apiBase("wallet/summary"));
            if (res?.unauth) {
                setError("Session expired. Please sign in again.");
                setRows([]);
                setMode("");
                setNote("");
                setOperatorNote("");
                return;
            }
            if (!res?.ok) {
                // eslint-disable-next-line no-console
                console.warn("[wallet] failed to load wallet totals", res);
                setError("Unable to load wallet totals.");
                setRows([]);
                setMode("");
                setNote("");
                setOperatorNote("");
                return;
            }
            const modeValue = (res.data?.mode || "").toString().toLowerCase();
            setMode(modeValue);
            setNote(res.data?.note || "");
            setOperatorNote(res.data?.operator_note || "");
            const coins = Array.isArray(res.data?.coins) ? res.data.coins : [];
            const seen = new Set();
            const bySymbol = new Map();
            coins.forEach((row) => {
                const canonical = resolveCanonicalSymbol(row?.canonical_symbol || row?.symbol || row?.coin);
                if (!canonical || seen.has(canonical)) return;
                seen.add(canonical);
                const entry = { ...row, coin: canonical };
                bySymbol.set(canonical, entry);
            });
            setRows(sortCanonicalSymbols([...bySymbol.keys()]).map((symbol) => bySymbol.get(symbol)));
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[wallet] request errored", err);
            setError("Unable to load wallet totals.");
            setRows([]);
            setMode("");
            setNote("");
            setOperatorNote("");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const hasData = useMemo(() => rows.length > 0, [rows]);

    const modeLabel = useMemo(() => {
        if (mode === "dry") return "Dry mode – balances disabled";
        if (mode === "paper") return "Paper balances (simulated)";
        if (mode === "live") return "Live balances (exchange)";
        return null;
    }, [mode]);

    return (
        <div className={`space-y-3 ${className}`} data-testid="wallet-totals-panel">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[0.65rem] uppercase tracking-wide text-gray-400">Aggregated balances</p>
                    <h3 className="text-lg font-semibold text-white">Wallet Totals</h3>
                    {(modeLabel || note) && (
                        <p className="text-xs text-gray-400" data-testid="wallet-mode-label">
                            {modeLabel || note}
                        </p>
                    )}
                    {operatorNote && (
                        <p className="text-xs text-gray-500" data-testid="wallet-operator-note">
                            {operatorNote}
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="px-3 py-1 rounded bg-gray-800 text-gray-100 border border-gray-700 hover:border-cyan-500"
                >
                    Refresh
                </button>
            </div>
            {loading && <div className="text-sm text-gray-400">Loading wallet totals...</div>}
            {!loading && error && (
                <div className="text-sm text-red-400" data-testid="wallet-error">
                    {error}
                </div>
            )}
            {!loading && !error && !hasData && (
                <div className="text-sm text-gray-400">No wallet data available yet.</div>
            )}
            {!loading && !error && hasData && (
                <div className="overflow-auto border border-gray-200/20 rounded-md" data-testid="wallet-table">
                    <table className="min-w-full divide-y divide-gray-800">
                        <thead className="bg-gray-800/40">
                            <tr className="text-left text-xs font-semibold text-gray-200">
                                <th className="py-2 px-3">Coin</th>
                                <th className="py-2 px-3 text-right">Position</th>
                                <th className="py-2 px-3 text-right">Cash</th>
                                <th className="py-2 px-3 text-right">Current Price</th>
                                <th className="py-2 px-3 text-right">Position Value</th>
                                <th className="py-2 px-3 text-right">Total Equity</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/60">
                            {rows.map((row) => {
                                const value = normalizedValuation(row);
                                return (
                                    <tr key={row.coin}>
                                        <td className="py-2 px-3 font-semibold">{row.coin}</td>
                                        <td className="py-2 px-3 text-right text-sm">
                                            {value.quantity == null ? "—" : value.quantity.toFixed(4)}
                                        </td>
                                        <td className="py-2 px-3 text-right text-sm">{formatUsd(value.cash)}</td>
                                        <td className="py-2 px-3 text-right text-sm">
                                            <div className="flex flex-col items-end">
                                                <span>{formatUsd(value.price)}</span>
                                                {value.priceUnavailable && (
                                                    <span className="text-[0.65rem] text-amber-300">Price unavailable</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 text-right text-sm">{formatUsd(value.positionValue)}</td>
                                        <td className="py-2 px-3 text-right text-sm">{formatUsd(value.totalEquity)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

WalletTotalsPanel.propTypes = {
    className: PropTypes.string,
};
